import { Link } from "react-router-dom";
import { ExternalLink, ArrowRight, ShieldCheck, AlertCircle } from "lucide-react";
import SEO from "../components/SEO";
import { MANUFACTURERS, PRODUCTS, HOW_CI_WORKS_DIAGRAM } from "../data/products";

export default function Technology() {
  return (
    <>
      <SEO
        title="Cochlear Implant Brands in India — Cochlear, MED-EL, Advanced Bionics"
        description="Detailed, neutral overview of the three major cochlear implant manufacturers in India — Cochlear (Nucleus 8), MED-EL (SYNCHRONY 2, SONNET 3, RONDO 3) and Advanced Bionics (Naída CI Marvel, Sky CI M). Genuine product images and information from official manufacturer sources."
      />

      {/* Header */}
      <section className="bg-gradient-to-br from-[#F0F9FF] via-white to-[#ECFDF5] py-14 md:py-20">
        <div className="hc-container">
          <div className="text-[#0F766E] uppercase tracking-wider text-[12px] font-bold mb-3">
            Technology & Products
          </div>
          <h1 className="font-serif text-[2.25rem] md:text-[3rem] font-semibold leading-[1.08] text-slate-900 max-w-4xl">
            Cochlear Implant Brands in India
          </h1>
          <p className="mt-5 text-slate-700 text-[1.08rem] leading-relaxed max-w-3xl">
            Three major manufacturers serve the Indian market —{" "}
            <strong>Cochlear Limited</strong> (Australia), <strong>MED-EL</strong> (Austria) and{" "}
            <strong>Advanced Bionics</strong> (USA/Sonova). All three offer modern, internationally
            approved implants and processors. Choice is decided clinically with your CI team.
          </p>

          <div className="mt-6 inline-flex items-start gap-2 bg-white border border-slate-200 rounded-xl px-4 py-3 text-[14px] text-slate-700">
            <AlertCircle size={18} className="text-[#0369A1] mt-0.5 shrink-0" />
            <span>
              <strong>Editorial note:</strong> Product names, descriptions and images shown below are
              owned by the respective manufacturers and used here for educational purposes. We do not
              endorse any single brand. Always consult your CI team for clinical decisions.
            </span>
          </div>
        </div>
      </section>

      {/* How a CI works */}
      <section className="hc-section">
        <div className="hc-container grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[#0F766E] uppercase tracking-wider text-[12px] font-bold mb-3">How a CI works</div>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              From sound to the brain
            </h2>
            <ol className="mt-6 space-y-3 text-[16px] text-slate-700">
              <li><strong>1.</strong> Microphones on the sound processor capture sound waves from the environment.</li>
              <li><strong>2.</strong> The processor converts sound into a precise digital signal.</li>
              <li><strong>3.</strong> The magnetic headpiece transmits the signal through the skin to the implant.</li>
              <li><strong>4.</strong> The implant directs the electrode array to stimulate the hearing nerve.</li>
              <li><strong>5.</strong> The hearing nerve sends impulses to the brain, where they are interpreted as sound.</li>
            </ol>
          </div>
          <img
            src={HOW_CI_WORKS_DIAGRAM}
            alt="How a cochlear implant works — diagram by Advanced Bionics"
            className="rounded-2xl border border-slate-200 w-full"
            data-testid="how-ci-works-diagram"
          />
        </div>
      </section>

      {/* Manufacturers + Products */}
      {MANUFACTURERS.map((m, idx) => {
        const ps = PRODUCTS.filter((p) => p.manufacturer === m.id);
        return (
          <section
            key={m.id}
            data-testid={`manufacturer-section-${m.id}`}
            className={`hc-section ${idx % 2 === 0 ? "bg-slate-50" : ""}`}
          >
            <div className="hc-container">
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-10 border-b border-slate-200 pb-6">
                <div>
                  <div className="text-[#0F766E] uppercase tracking-wider text-[12px] font-bold mb-2">
                    Manufacturer #{idx + 1}
                  </div>
                  <h2 className="font-serif text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
                    {m.name}
                  </h2>
                  <p className="mt-1 text-slate-500 text-[15px]">{m.country} · <em>{m.tagline}</em></p>
                </div>
                <a
                  href={m.indiaWebsite}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`manufacturer-site-${m.id}`}
                  className="btn-secondary text-sm py-2.5 px-5 inline-flex items-center gap-1.5 shrink-0"
                >
                  Official site <ExternalLink size={16} />
                </a>
              </div>

              <p className="text-slate-700 text-[17px] leading-relaxed max-w-3xl">{m.description}</p>

              <div className="mt-10 grid md:grid-cols-2 gap-5">
                {ps.map((p) => (
                  <Link
                    key={p.id}
                    to={`/technology/${p.id}`}
                    data-testid={`product-card-${p.id}`}
                    className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg hover:border-[#0369A1] transition group"
                  >
                    <div className="aspect-[5/3] bg-gradient-to-br from-slate-50 to-white flex items-center justify-center p-6 border-b border-slate-100">
                      <img
                        src={p.image}
                        alt={p.name}
                        className="max-h-full max-w-full object-contain"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-6">
                      <div className="text-[11px] uppercase tracking-wider text-[#0F766E] font-bold">
                        {p.category}
                      </div>
                      <div className="font-serif text-[1.2rem] text-slate-900 font-semibold mt-1.5 leading-snug">
                        {p.name}
                      </div>
                      <p className="mt-2 text-slate-600 text-[14.5px] leading-relaxed">{p.short}</p>

                      <div className="mt-4 flex flex-wrap gap-1.5">
                        {p.audience.map((a) => (
                          <span key={a} className="chip bg-[#F0F9FF] text-[#0369A1]">
                            {a === "child" ? "Children" : "Adults"}
                          </span>
                        ))}
                      </div>

                      <div className="mt-5 flex items-center justify-between text-[13px]">
                        <span className="text-slate-500 inline-flex items-center gap-1">
                          <ShieldCheck size={14} /> Genuine — from manufacturer site
                        </span>
                        <span className="text-[#0369A1] font-semibold inline-flex items-center gap-1">
                          View details <ArrowRight size={14} />
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        );
      })}

      {/* Disclaimer block */}
      <section className="hc-section">
        <div className="hc-container">
          <div className="bg-slate-50 border border-slate-200 rounded-2xl p-7">
            <h3 className="font-serif text-xl font-semibold text-slate-900">Important disclaimer</h3>
            <p className="mt-3 text-slate-700 text-[15px] leading-relaxed">
              Product photographs and text excerpts on this page are sourced directly from the
              official manufacturer websites (Cochlear India, MED-EL global, Advanced Bionics global)
              and are the property of the respective manufacturers. We display them here strictly for
              educational and patient-awareness purposes under fair-use editorial reference.
              For complete, up-to-date and country-approved information, please always refer to the
              official sources linked on each product. Cochlear implant device selection should be
              decided clinically — never on this site alone.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
